import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { AddressLabel, SavedAddress } from 'mobx/Localization/types'
import { useEffect, useRef } from 'react'
import { getTranslatedTextByKey } from 'utils/language'
import { StyledArrowBack, StyledCloseButton, StyledCloseIcon } from '../OrderTypeSelection/styles'
import {
	StyledManualAddressButton,
	StyledManualAddressButtonText,
	StyledPlusIcon,
	StyledSaveAddressesAddNewAddressButton,
	StyledSavedAddressButton,
	StyledSavedAddressButtonIcon,
	StyledSavedAddressesContainer,
	StyledSavedAddressesHeader,
	StyledSavedAddressesList,
	StyledSavedAddressesPlaceholderIcon,
	StyledSavedAddressesPlaceholderText,
	StyledSavedAddressesTitle,
	StyledSavedAddressSubtitle,
	StyledSavedAddressTextsContainer,
	StyledSavedAddressTitle,
} from './SavedAddresses.styles'
import type _Infra from 'mobx/Infra'
import { injectStores } from 'mobx/injection'

interface SavedAddressesProps {
	goToAddressEntry: () => void
  goBack: () => void
	Infra: typeof _Infra
}

const labelIconMap: Record<AddressLabel, string> = {
	[AddressLabel.Home]: '/icons/homeLabel.svg',
	[AddressLabel.Work]: '/icons/workLabel.svg',
	[AddressLabel.Other]: '/icons/otherLabel.svg',
}

const getAddressTitle = (address: SavedAddress) => {
	if (address.label === AddressLabel.Work) {
		return getTranslatedTextByKey('webviewFlow.localization.workLabel', 'Work')
	}

	if (address.label === AddressLabel.Home) {
		return getTranslatedTextByKey('webviewFlow.localization.homeLabel', 'Home')
	}

	return address.customLabel || getTranslatedTextByKey('webviewFlow.localization.otherLabel', 'Other')
}

const SavedAddresses = ({ goToAddressEntry, goBack, Infra }: SavedAddressesProps) => {
	const { localizationStore } = useStores()
	const selectedAddressRef = useRef<HTMLButtonElement | null>(null)
	const { savedAddresses, reachedMaximumSavedAddresses } = localizationStore
	const selectedSavedAddressId = localizationStore.processingFlow?.savedAddressId

	const onSavedAddressClick = async (address: SavedAddress) => {
		localizationStore.processingFlow?.selectSavedAddress(address)

		Infra.setLoading(true)
		await localizationStore.matchStoreForCurrentAddress()
		Infra.setLoading(false)
	}

	const onSaveNewAddressClick = () => {
		localizationStore.startSavingAddress()
		localizationStore.processingFlow?.unselectSavedAddress()
		goToAddressEntry()
	}

	const onEnterManuallyClick = () => {
		localizationStore.stopSavingAddress()
		localizationStore.processingFlow?.unselectSavedAddress()
		goToAddressEntry()
	}

	useEffect(() => {
		selectedAddressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
	}, [])

	return (
		<StyledSavedAddressesContainer>
			<StyledSavedAddressesHeader>
				<StyledArrowBack onClick={goBack} />
				<StyledSavedAddressesTitle variant="SubheadHighlighted">
					{getTranslatedTextByKey('webviewFlow.localization.savedAddresses', 'Saved addresses')}
				</StyledSavedAddressesTitle>
				<StyledCloseButton onClick={localizationStore.abort}>
					<StyledCloseIcon />
				</StyledCloseButton>
			</StyledSavedAddressesHeader>
			{savedAddresses.length === 0 && (
				<>
					<StyledSavedAddressesPlaceholderIcon asset="/icons/mapPinGrey.svg" />
					<StyledSavedAddressesPlaceholderText variant="BodyRegular">
						{getTranslatedTextByKey('webviewFlow.localization.noSavedAddresses', 'There aren’t any saved addresses in your account yet!')}
					</StyledSavedAddressesPlaceholderText>
				</>
			)}

			<StyledSavedAddressesList>
				{savedAddresses.map((address) => (
					<StyledSavedAddressButton
						ref={address._id === selectedSavedAddressId ? selectedAddressRef : undefined}
						selected={selectedSavedAddressId === address._id}
						onClick={() => onSavedAddressClick(address)}
						key={address._id}
					>
						<StyledSavedAddressButtonIcon asset={labelIconMap[address.label]} />
						<StyledSavedAddressTextsContainer>
							<StyledSavedAddressTitle variant="SubheadHighlighted">{getAddressTitle(address)}</StyledSavedAddressTitle>
							<StyledSavedAddressSubtitle variant="BodyRegular">{address.formatted}</StyledSavedAddressSubtitle>
						</StyledSavedAddressTextsContainer>
					</StyledSavedAddressButton>
				))}
			</StyledSavedAddressesList>

			<StyledSaveAddressesAddNewAddressButton $type="secondary" onClick={onSaveNewAddressClick} disabled={reachedMaximumSavedAddresses}>
				<StyledPlusIcon />
				{getTranslatedTextByKey('webviewFlow.localization.addNewAddress', 'Add new address')}
			</StyledSaveAddressesAddNewAddressButton>
			<StyledManualAddressButton>
				<StyledManualAddressButtonText variant="CTATextSmall" onClick={onEnterManuallyClick}>
					{getTranslatedTextByKey('webviewFlow.localization.addAddressManually', 'Enter address manually')}
				</StyledManualAddressButtonText>
			</StyledManualAddressButton>
		</StyledSavedAddressesContainer>
	)
}

export default injectStores('Infra')(observer(SavedAddresses))
